import { ReactElement, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { PrimaryButton, SecondaryButton, SnackbarStatus, Typography, useSnackbar } from '@weareopenr/component-library';
import { createTimetables } from 'src/api/createTimetables/createTimetables';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useMenus } from 'src/hooks/useMenus';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { useTimetables } from 'src/hooks/useTimetables';
import { useTimetableRangesEnabled } from 'src/hooks/useTimetableRangesEnabled';
import { filterMenusByRanges } from 'src/utils/timetables/helpers';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { TimetableNameField } from './fields/TimetableNameField';
import { MenuSourceField } from './fields/MenuSourceField';
import { TimetableDefaultsFields } from './fields/TimetableDefaultsFields';
import { ICreateTimetableFormValues } from './types';
import styles from './CreateTimetableModal.module.scss';

const EXISTING_NAMES_STALE_TIME = 5 * 60 * 1000;

interface CreateTimetableModalContentProps {
  closeModal: () => void;
}

export const CreateTimetableModalContent = ({ closeModal }: CreateTimetableModalContentProps): ReactElement => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { data: menus = [], isLoading: isMenusLoading } = useMenus();
  const { data: priceSchemes = [], isLoading: isPriceSchemesLoading } = usePriceSchemes();
  const { data: ranges = [], isLoading: isRangesLoading } = useRanges();
  const { isEnabled: rangesEnabled } = useTimetableRangesEnabled();
  const { data: timetablesData } = useTimetables(15, undefined, undefined, { staleTime: EXISTING_NAMES_STALE_TIME });
  const existingTimetableNames = useMemo(
    () => timetablesData?.pages.flatMap((page) => page.data.map((timetable) => timetable.name)) ?? [],
    [timetablesData],
  );
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ICreateTimetableFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      menuSource: 'all',
      defaultMenuId: '',
      defaultPriceSchemeId: '',
      rangeIds: [],
    },
  });

  const menuSource = watch('menuSource');
  const isRangesSource = menuSource === 'ranges';
  const rangeIds = watch('rangeIds');
  const defaultMenuId = watch('defaultMenuId');

  // Once ranges are chosen, only menus in at least one of them make sense as the default -
  // narrows the same way the Timetable page narrows menus for an existing timetable's ranges.
  const selectedRanges = useMemo(
    () => (isRangesSource ? ranges.filter((range) => rangeIds.includes(range.range_id)) : []),
    [isRangesSource, ranges, rangeIds],
  );
  const defaultMenuOptions = useMemo(
    () => (isRangesSource ? filterMenusByRanges(menus, selectedRanges) : menus),
    [isRangesSource, menus, selectedRanges],
  );

  // If a previously chosen default menu falls outside the newly selected ranges, it's no longer
  // a valid choice and would otherwise sit selected but absent from the dropdown.
  useEffect(() => {
    if (defaultMenuId && !defaultMenuOptions.some((menu) => menu.menuId === defaultMenuId)) {
      setValue('defaultMenuId', '', { shouldValidate: true });
    }
  }, [defaultMenuOptions, defaultMenuId, setValue]);

  const onSubmit = async (values: ICreateTimetableFormValues) => {
    try {
      await createTimetables({
        name: values.name.trim(),
        default_menu_id: values.defaultMenuId,
        default_price_scheme_id: values.defaultPriceSchemeId,
        ...(isRangesSource && { range_ids: values.rangeIds }),
      });

      await queryClient.invalidateQueries({ queryKey: [QueryKeys.timetables] });
      showSnackbar({ status: SnackbarStatus.SUCCESS, message: 'Timetable created' });
      closeModal();
    } catch (error) {
      const message = error instanceof Error ? error.message : undefined;
      showSnackbar({ status: SnackbarStatus.ERROR, message: message || 'Something went wrong. Please try again.' });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles['content']}>
      <div>
        <Typography size="xxlarge" weight="bold" className={styles['title']}>
          Create a timetable
        </Typography>

        <TimetableNameField register={register} errors={errors} existingNames={existingTimetableNames} />
      </div>

      <MenuSourceField
        register={register}
        control={control}
        isRangesSource={isRangesSource}
        rangesEnabled={rangesEnabled}
        ranges={ranges}
        isRangesLoading={isRangesLoading}
      />

      <TimetableDefaultsFields
        control={control}
        menus={defaultMenuOptions}
        isMenusLoading={isMenusLoading}
        priceSchemes={priceSchemes}
        isPriceSchemesLoading={isPriceSchemesLoading}
      />

      <ButtonContainer
        left={
          <SecondaryButton buttonStyle="outline" type="button" size="full-width" onClick={closeModal}>
            Cancel
          </SecondaryButton>
        }
        right={
          <PrimaryButton type="submit" size="full-width" loading={isSubmitting} disabled={isSubmitting || !isValid}>
            Create
          </PrimaryButton>
        }
      />
    </form>
  );
};

export default CreateTimetableModalContent;
